// client/src/components/pages/RouteIntelligence/varianceMatched.ts (ST-00229, audit ANL-14)
//
// The variance % is computed from the matched dispatch/receipt pairs only (Delivery No =
// Ticket No), not from the day totals. Migration 055 stores the matched weights and count next
// to the day totals; for a day not recalculated since then they are null, and the screens show
// the day totals alone.
import type { NumericValue } from '../../../types/clientPages/routeIntelligence';

export interface MatchedFigures {
  dispatchWeight: number;
  receiptWeight: number;
  count: number;
  /** Day loads with no matched partner (never below 0). */
  unmatchedDispatches: number;
  unmatchedReceipts: number;
}

const num = (v: NumericValue | null | undefined): number | null => {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/** The matched figures of a day, or null when the day has none stored (pre-055 rows). */
export const matchedFigures = (row: {
  matchedDispatchWeight: NumericValue | null | undefined;
  matchedReceiptWeight: NumericValue | null | undefined;
  matchedCount: number | null | undefined;
  dispatchCount: number | null | undefined;
  receiptCount: number | null | undefined;
}): MatchedFigures | null => {
  const dispatchWeight = num(row.matchedDispatchWeight);
  const receiptWeight = num(row.matchedReceiptWeight);
  const count = row.matchedCount;
  if (dispatchWeight == null || receiptWeight == null || count == null) return null;
  return {
    dispatchWeight,
    receiptWeight,
    count,
    unmatchedDispatches: Math.max(0, (row.dispatchCount ?? 0) - count),
    unmatchedReceipts: Math.max(0, (row.receiptCount ?? 0) - count),
  };
};

/** "12 dispatches and 40 receipts unmatched" (empty when every load matched). */
export const unmatchedText = (m: MatchedFigures): string => {
  const parts: string[] = [];
  if (m.unmatchedDispatches)
    parts.push(`${m.unmatchedDispatches} dispatch${m.unmatchedDispatches === 1 ? '' : 'es'}`);
  if (m.unmatchedReceipts)
    parts.push(`${m.unmatchedReceipts} receipt${m.unmatchedReceipts === 1 ? '' : 's'}`);
  return parts.length ? `${parts.join(' and ')} unmatched` : '';
};
